import { useContext, useEffect, useMemo, useState } from "react";
import Navbar from "../../../../../common/layout/Navbar";
import {
  GridAlignment,
  GridColDef,
  GridColumnVisibilityModel,
  GridValidRowModel,
} from "@mui/x-data-grid-pro";
import { LicenseTypes, Operation } from "../../../../../../utils/Enum.types";
import { Title } from "../../../../../../stories/Title/Title";
import { Button } from "../../../../../../stories/Button/Button";
import Input from "../../../../../../stories/Input/Input";
import { useNavigate } from "react-router-dom";
import { TableHeaderProps } from "../../../../../../utils/types";
import useAssetTableJson from "../../../../../../json/useAssetTableJson";
import Edit from "../../../../../../images/EditProfile.svg";
import Delete from "../../../../../../images/Delete.svg";
import { ColumnVisibilityFor } from "../../../../../../utils/Enum.Datagrid";
import { StyledDatagrid } from "../../../../../../styles/DatagridStyles";
import { CustomModal } from "../../../../../../utils/UtilFunctions";
import {
  ViewModalStyles,
  WaiveOffModalStyles,
} from "../../../../../../styles/CustomModalStyles";
import { useHotKey } from "../../../../../../utils/hotKeys";
import { useLazyQuery, useMutation } from "@apollo/client";
import { GetItemGroupAssemblyDetails } from "../queries/queries";
import useUrlParams from "../../../../../../customHooks/useUrlParams";
import {
  AssemblyItemDetailsData,
  DeleteItemGroupAssemblyResult,
  DeleteItemGroupAssemblyVars,
  GetItemGroupAssemblyDetailsDataType,
  GetItemGroupAssemblyDetailsResponse,
  GetItemGroupAssemblyDetailsVars,
} from "../queries/types";
import { AppContext } from "../../../../../../context/context";
import { payloadTypes } from "../../../../../../context/reducer";
import AssociateGroupItemsDetails from "./AssociateGroupItemsDetails";
import View from "../../../../../../images/Eye.svg";
import { DeleteItemGroupAssembly } from "../queries/mutation";
import { emptyMessage } from "../../../../../../utils/constants";
import MessageModal from "../../../../../../customComponents/MessageModal";
import DeleteModal from "../../../../../../customComponents/DeleteModal";
import useLoggedInUserDetails from "../../../../../../customHooks/useLoggedInUserDetails";

export const enum GroupingListPageFor {
  GENERAL = "GENERAL",
  ASSIGN = "ASSIGN",
}

export interface AssignAssetProps {
  flag: boolean;
  operation: Operation;
}

interface ItemDetailsData {
  groupName: string;
  disc: string;
  ItemDetails: AssemblyItemDetailsData[];
}

const GroupAssembliesList = () => {
  const navigate = useNavigate();
  const { Masters_Table } = useAssetTableJson();
  const [assignAsset, setAssignAsset] = useState<AssignAssetProps>({
    flag: false,
    operation: Operation.NONE,
  });

  const { user_details } = useLoggedInUserDetails();
  const [message, setMessage] = useState(emptyMessage);
  const [searchData, setSearchData] = useState("");
  const { customer_id, group_id } = useUrlParams();
  const [itemData, setItemData] =
    useState<GetItemGroupAssemblyDetailsDataType>();

  const [deleteModal, setDeleteModal] = useState(false);

  const [deletedId, setDeletedId] = useState(0);

  const { dispatch } = useContext(AppContext);

  const [itemDetailsList, setItemsDetailsList] = useState(false);

  const [item, setItem] = useState<ItemDetailsData>({
    disc: "",
    groupName: "",
    ItemDetails: [],
  });

  const [GetAssocs, { data }] = useLazyQuery<
    GetItemGroupAssemblyDetailsResponse,
    GetItemGroupAssemblyDetailsVars
  >(GetItemGroupAssemblyDetails);

  const [DeleteAssembly] = useMutation<
    DeleteItemGroupAssemblyResult,
    DeleteItemGroupAssemblyVars
  >(DeleteItemGroupAssembly, {
    onError: (e) => {
      setMessage({
        flag: true,
        message: e.message,
        operation: Operation.NONE,
      });
    },
  });

  useEffect(() => {
    if (customer_id && group_id) {
      GetAssocs({
        variables: {
          customerID: customer_id,
          itemGrpAssmbldID: 0,
          itemGrpID: group_id,
        },
      });
    }
  }, [customer_id, GetAssocs, searchData, group_id]);

  const dynamicHeaders: TableHeaderProps[] =
    Masters_Table.Masters.Grouping.Asset.map((header) => ({
      headerName: header.headerName,
      field: header.field,
      headerAlign: header.headerAlign as GridAlignment,
      align: header.align as GridAlignment,
      flex: header.flex,
      hideable: header.hideable,
      cellClassName: header.cellClassName,

      renderCell: (params) => {
        if (header.field === "actions") {
          return (
            <>
              <img
                src={View}
                onClick={() => {
                  setItemData(params.row);
                  setItemsDetailsList(!itemDetailsList);
                }}
                alt=""
              />
              <img
                src={Edit}
                alt=""
                onClick={() => {
                  dispatch({
                    type: payloadTypes.SET_GRP_ASSEMBLY_ID,
                    payload: {
                      grpAssemblyId: params.row.assemblyId,
                    },
                  });
                  setAssignAsset({
                    flag: true,
                    operation: Operation.UPDATE,
                  });
                }}
              />
              <img
                src={Delete}
                alt=""
                onClick={() => {
                  setDeletedId(params.row.assemblyId);
                  setDeleteModal(!deleteModal);
                }}
              />
            </>
          );
        } else if (header.field === "asset_list") {
          return (
            <ul className="grouping-asset__ul">
              {params.value.map((itm: any) => {
                return (
                  <li className="grouping-asset__ul--flex">
                    <div>{itm.desc} </div>
                  </li>
                );
              })}
            </ul>
          );
        } else if (header.field === "") return params.value;
      },
    }));
  const columns: GridColDef[] = [...dynamicHeaders];

  const rows: GridValidRowModel[] = useMemo(() => {
    return data
      ? data.GetItemGroupAssemblyDetails.filter((item) =>
          item.mapNo.toLowerCase().includes(searchData.toLowerCase())
        ).map((itm, index) => ({
          id: index + 1,
          grouping_name: itm.mapNo,
          asset_list: itm.AssemblyItem.map((itemDetails) => ({
            desc: itemDetails.desc,
            itemDetails: itemDetails.AssemblyItemDetails,
          })),
          total_assets: itm.totalQty,
          grpId: itm.itemGrpID,
          assemblyId: itm.id,
        }))
      : [];
  }, [data, searchData]);

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({});

  useEffect(() => {
    const savedModel = localStorage.getItem(ColumnVisibilityFor.GROUPING_ASSET);
    if (savedModel) {
      setColumnVisibilityModel(JSON.parse(savedModel));
    }
  }, []);

  const handleColumnVisibilityChange = (
    newModel: GridColumnVisibilityModel
  ) => {
    setColumnVisibilityModel(newModel);
    localStorage.setItem(
      ColumnVisibilityFor.GROUPING_ASSET,
      JSON.stringify(newModel)
    );
  };

  useHotKey({
    charater: "a",
    handleFunc: () => {
      setAssignAsset({
        flag: true,
        operation: Operation.CREATE,
      });
    },
    specialCharacter: "ctrlKey",
  });

  const handleClose = () => {
    setMessage(emptyMessage);
  };

  const handleDelete = (id: number) => {
    setDeleteModal(!deleteModal);
    DeleteAssembly({
      variables: {
        itemGrpAssmbldID: id,
        user_details,
      },
      refetchQueries: [
        {
          query: GetItemGroupAssemblyDetails,
          variables: {
            customerID: customer_id,
            itemGrpAssmbldID: 0,
            itemGrpID: group_id,
          },
        },
      ],
    }).then(({ data }) => {
      if (data && data.DeleteItemGroupAssembly) {
        setMessage({
          flag: true,
          message: "Deleted Successfully",
          operation: Operation.CREATE,
        });
      }
    });
  };

  return (
    <>
      <Navbar NavType={LicenseTypes.COMPANY} />
      <Title>Grouping Asset</Title>
      <div className="grouping-asset">
        <div className="grouping-asset__search row g-0">
          <div className="col-2">
            <Input
              id="data_search"
              placeholder="Search..."
              onChange={(e) => {
                setSearchData(e.target.value);
              }}
            />
          </div>
          <div className="col"></div>
          <div className="col-2 flex-end">
            <Button
              mode="addnew"
              onClick={() => {
                setAssignAsset({
                  flag: true,
                  operation: Operation.CREATE,
                });
              }}
            />
          </div>
        </div>
        <div className="grouping-asset__tableblock">
          <StyledDatagrid
            columns={columns}
            rows={rows}
            rowHeight={100}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={handleColumnVisibilityChange}
            initialState={{
              columns: {
                columnVisibilityModel: columnVisibilityModel,
              },
            }}
            disableRowSelectionOnClick
            hideFooter
          />
        </div>
        <Button mode="back" onClick={() => navigate(-1)} />
      </div>
      {/* //to be viewed padma */}
      <CustomModal
        style={WaiveOffModalStyles}
        isOpen={itemDetailsList}
        onRequestClose={() => setItemsDetailsList(!itemDetailsList)}
        contentComponent={
          itemData ? (
            <div>
              <Title>{itemData.grouping_name}</Title>

              {itemData.asset_list
                ? itemData.asset_list.map((item) => (
                    <ul>
                      <b>Asset : {item.desc}</b>
                      {item.itemDetails
                        ? item.itemDetails.map((itm) => {
                            return <li> SerialNo :{itm.prodSerialNo}</li>;
                          })
                        : null}
                    </ul>
                  ))
                : null}
            </div>
          ) : null
        }
      />
      <CustomModal
        style={ViewModalStyles}
        isOpen={assignAsset.flag}
        onRequestClose={() =>
          setAssignAsset({
            flag: false,
            operation: Operation.NONE,
          })
        }
        contentComponent={
          <AssociateGroupItemsDetails
            setModal={setAssignAsset}
            operation={assignAsset.operation}
          />
        }
      />

      <MessageModal
        handleClose={handleClose}
        modalFlag={message.flag}
        operation={message.operation}
        value={message.message}
      />

      <DeleteModal
        id={deletedId}
        modalFlag={deleteModal}
        setModalFlag={setDeleteModal}
        handleDelete={handleDelete}
      />
    </>
  );
};

export default GroupAssembliesList;
